import { useEffect, useState } from "react";
import axios from "axios";
import { Pencil, Trash2 } from "lucide-react";
import { baseUrl } from "../../common/constants";
import _axios from "../../utils/api/_axios";
import ButtonLoader from "../../components/ReusableElements/Button/ButtonLoader";
import { toast } from "react-toastify";

const DoctorsManagement = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editId, setEditId] = useState(null);
  const [doctors, setDoctors] = useState([]);
  const [networkLoading, setNetworkLoading] = useState(false);
  const [files, setFiles] = useState([]);
  const [existingFiles, setExistingFiles] = useState([]);
  const [removedFiles, setRemovedFiles] = useState([]);
  const [doctorDetails, setDoctorDetails] = useState({
    physicianPrefix: "Dr.",
    physicianFirstName: "",
    physicianLastName: "",
    physicianEmail: "",
    physicianPhone: "",
    physicianNPI: "",
    physicianLicense: "",
    physicianWCAuthorization: "",
    physicianServices: [],
    physicianEIN: "",
    physicianEINType: "",
  });

  useEffect(() => {
    _axios
      .get(`${baseUrl}/physician/get-all`)
      .then((response) => {
        setDoctors(response.data.payload);
      })
      .catch((error) => console.error("Error fetching doctors:", error));
  }, []);

  const openModal = () => {
    setIsEditing(false);
    setDoctorDetails({
      physicianPrefix: "Dr.",
      physicianFirstName: "",
      physicianLastName: "",
      physicianEmail: "",
      physicianPhone: "",
      physicianNPI: "",
      physicianLicense: "",
      physicianWCAuthorization: "",
      physicianServices: [],
      physicianEIN: "",
      physicianEINType: "",
    });
    setFiles([]);
    setExistingFiles([]);
    setRemovedFiles([]);
    setIsModalOpen(true);
  };

  const openEditModal = (doctor) => {
    setIsEditing(true);
    setEditId(doctor._id);
    setDoctorDetails({
      physicianPrefix: doctor.physicianPrefix || "Dr.",
      physicianFirstName: doctor.physicianFirstName,
      physicianLastName: doctor.physicianLastName,
      specialization: doctor.specialization,
      physicianEmail: doctor.physicianEmail,
      physicianPhone: doctor.physicianPhone,
      physicianNPI: doctor.physicianNPI,
      physicianLicense: doctor.physicianLicense,
      physicianWCAuthorization: doctor.physicianWCAuthorization,
      physicianServices: doctor.physicianServices || [],
      physicianEIN: doctor.physicianEIN || "",
      physicianEINType: doctor.physicianEINType || "",
    });
    setFiles([]); 
    setExistingFiles(doctor.physicianDocs || []);
    setRemovedFiles([]);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditId(null);
    setRemovedFiles([]);
  };

  const formatPhoneNumber = (value) => {
    const cleaned = value.replace(/\D/g, "");
    if (cleaned.length <= 3) {
      return cleaned;
    } else if (cleaned.length <= 6) {
      return `${cleaned.slice(0, 3)}-${cleaned.slice(3)}`;
    } else {
      return `${cleaned.slice(0, 3)}-${cleaned.slice(3, 6)}-${cleaned.slice(6, 10)}`;
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    const formattedValue = name === "physicianPhone" ? formatPhoneNumber(value) : value;

    setDoctorDetails((prev) => ({ ...prev, [name]: formattedValue }));
  };

  const handleFileChange = (e) => {
    setFiles((prevFiles) => [...prevFiles, ...Array.from(e.target.files)]);
  };

  const handleRemoveExistingFile = (fileName) => {
    setExistingFiles((prevFiles) => prevFiles.filter((file) => file.name !== fileName));
    setRemovedFiles((prevRemovedFiles) => [...prevRemovedFiles, fileName]);
  };

  const handleSaveDoctor = async (e) => {
    e.preventDefault();

    if (!doctorDetails.physicianFirstName || !doctorDetails.physicianLastName || !doctorDetails.physicianEmail || !doctorDetails.physicianPhone) {
      alert("Please fill in all required fields.");
      return;
    }

    try {
      setNetworkLoading(true);
      const formData = new FormData();
      files.forEach((file) => formData.append("files", file));
      debugger;
      Object.keys(doctorDetails).forEach((key) => formData.append(key, doctorDetails[key]));

      if (isEditing && removedFiles.length > 0) {
        formData.append("deleteDocs", JSON.stringify(removedFiles));
      }

      let responseMain;
      if (isEditing) {
        responseMain = await axios.put(`${baseUrl}/physician/${editId}`, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      } else {
        responseMain = await axios.post(`${baseUrl}/physician`, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      }

      setNetworkLoading(false);
      if (responseMain.status === 200 || responseMain.status === 201) {
        toast.success("Doctor operation successful");
        const response = await _axios.get(`${baseUrl}/physician/get-all`);
        setDoctors(response.data.payload);
        closeModal();
      }
    } catch (error) {
      console.error("Error saving doctor:", error);
      toast.error(error.response.data.error);
      setNetworkLoading(false);
    }
  };

  const handleDeleteDoctor = async (id) => {
    const confirmDelete = window.confirm("Are you sure you want to delete this doctor?");
    if (confirmDelete) {
      try {
        await _axios.delete(`${baseUrl}/physician/${id}`);
        window.location.reload();
      } catch (error) {
        console.error("Error deleting doctor:", error);
      }
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 p-6 flex justify-center">
      <div className="max-w-8xl w-full bg-white p-6 rounded-lg shadow-md">
        <h1 className="text-2xl font-semibold text-center text-gray-800 mb-6">
          Physicians Management
        </h1>

        <button
          onClick={openModal}
          className="bg-gray-600 hover:bg-gray-400 text-white px-6 py-3 rounded mb-6"
        >
          + Add Physician
        </button>

        {doctors?.length > 0 ? (
          <table className="w-full border-collapse bg-white shadow-md rounded-lg overflow-hidden">
            <>
              <thead>
                <tr className="bg-gray-200 text-gray-700">
                  <th className="p-4 text-left">Name</th>
                  <th className="p-4 text-left">Email</th>
                  <th className="p-4 text-left">Phone</th>
                  <th className="p-4 text-left">License Number</th>
                  <th className="p-4 text-left">Documents</th>
                  <th className="p-4 text-left">Actions</th>
                </tr>
              </thead>
              <tbody>
                {doctors?.map((doctor) => (
                  <tr key={doctor._id} className="border-b hover:bg-gray-100">
                    <td className="p-4">{`${doctor.physicianPrefix} ${doctor.physicianFirstName} ${doctor.physicianLastName}`}</td>
                    <td className="p-4">{doctor.physicianEmail}</td>
                    <td className="p-4">{doctor.physicianPhone}</td>
                    <td className="p-4">{doctor.physicianLicense}</td>
                    <td className="p-4">
                      {doctor.physicianDocs?.length > 0 ? (
                        <ul className="list-disc list-inside">
                          {doctor.physicianDocs.map((doc, index) => (
                            <li key={index}>
                              <a
                                href={doc.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-blue-600 hover:underline"
                              >
                                {doc.name}
                              </a>
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <span className="text-gray-500">No documents</span>
                      )}
                    </td>
                    <td className="p-4 flex gap-2">
                      <button
                        onClick={() => openEditModal(doctor)}
                        className="text-gray-600 hover:text-yellow-500 transition duration-200"
                      >
                        <Pencil size={20} />
                      </button>
                      <button
                        onClick={() => handleDeleteDoctor(doctor._id)}
                        className="text-gray-600 hover:text-red-500 transition duration-200"
                      >
                        <Trash2 size={20} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </>
          </table>
        ) : (
          <div className="text-center text-xl mt-6 opacity-25">No doctors available</div>
        )}
      </div>

      {/* Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-40 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg shadow-lg w-full max-w-lg relative">
            {/* Close button */}
            <button
              type="button"
              onClick={closeModal}
              className="absolute top-3 right-3 text-2xl text-gray-600 hover:text-gray-800"
            >
              &times;
            </button>

            <h2 className="text-2xl font-semibold mb-5 text-center">
              {isEditing ? "Edit Physician" : "Add New Physician"}
            </h2>

            <form onSubmit={handleSaveDoctor} className="space-y-4">
              {/* Prefix */}
              <select
                name="physicianPrefix"
                value={doctorDetails.physicianPrefix}
                onChange={handleInputChange}
                className="p-3 border border-gray-300 rounded w-full"
              >
                <option value="">--Select Prefix--</option>
                <option value="Dr.">Dr.</option>
                <option value="Mr.">Mr.</option>
                <option value="Ms.">Ms.</option>
              </select>

              {/* First Name & Last Name */}
              <div className="flex gap-4">
                <input
                  type="text"
                  name="physicianFirstName"
                  placeholder="First Name"
                  value={doctorDetails.physicianFirstName}
                  onChange={handleInputChange}
                  className="p-3 border border-gray-300 rounded w-1/2"
                  required
                />
                <input
                  type="text"
                  name="physicianLastName"
                  placeholder="Last Name"
                  value={doctorDetails.physicianLastName}
                  onChange={handleInputChange}
                  className="p-3 border border-gray-300 rounded w-1/2"
                  required
                />
              </div>

              {/* Specialization */}
              <input
                type="text"
                name="specialization"
                placeholder="Specialization"
                value={doctorDetails.specialization}
                onChange={handleInputChange}
                className="p-3 border border-gray-300 rounded w-full"
                required
              />

              {/* Email */}
              <input
                type="email"
                name="physicianEmail"
                placeholder="Email"
                value={doctorDetails.physicianEmail}
                onChange={handleInputChange}
                className="p-3 border border-gray-300 rounded w-full"
                required
              />

              {/* Phone */}
              <input
                type="text"
                name="physicianPhone"
                placeholder="Phone"
                value={doctorDetails.physicianPhone}
                onChange={handleInputChange}
                className="p-3 border border-gray-300 rounded w-full"
                required
              />

              {/* NPI & License */}
              <div className="flex gap-4">
                <input
                  type="text"
                  name="physicianNPI"
                  placeholder="NPI"
                  value={doctorDetails.physicianNPI}
                  onChange={handleInputChange}
                  className="p-3 border border-gray-300 rounded w-1/2"
                  required
                />
                <input
                  type="text"
                  name="physicianLicense"
                  placeholder="License"
                  value={doctorDetails.physicianLicense}
                  onChange={handleInputChange}
                  className="p-3 border border-gray-300 rounded w-1/2"
                  required
                />
              </div>

              <div className="flex gap-4">
                 <select
                  name="physicianEINType"
                  value={doctorDetails.physicianEINType || "SSN"}
                  onChange={handleInputChange}
                  className="p-3 border border-gray-300 rounded w-full"
                >
                  <option value="SSN">SSN</option>
                  <option value="EIN">EIN</option>
                </select>
                <input
                  type="text"
                  name="physicianEIN"
                  placeholder="EIN Number"
                  value={doctorDetails.physicianEIN}
                  onChange={handleInputChange}
                  className="p-3 border border-gray-300 rounded w-1/2"
                  required
                />
              </div>

              {/* Workers' Compensation Authorization */}
              <input
                type="text"
                name="physicianWCAuthorization"
                placeholder="Workers' Comp Authorization"
                value={doctorDetails.physicianWCAuthorization}
                onChange={handleInputChange}
                className="p-3 border border-gray-300 rounded w-full"
                required
              />

              {/* Existing Files */}
              {isEditing && existingFiles.length > 0 && (
                <div className="mt-4">
                  <h3 className="font-medium mb-2">Existing Files:</h3>
                  <ul className="list-disc list-inside text-gray-700">
                    {existingFiles.map((file, index) => (
                      <li key={index} className="flex items-center justify-between">
                        <a
                          href={file.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:underline"
                        >
                          {file.name}
                        </a>
                        <button
                          type="button"
                          onClick={() => handleRemoveExistingFile(file.name)}
                          className="text-red-600 hover:text-red-800"
                        >
                          Remove
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* File Upload */}
              <div className="flex flex-col gap-2">
                <label htmlFor="files" className="font-medium">
                  Upload New Documents
                </label>
                <input
                  type="file"
                  id="files"
                  multiple
                  onChange={handleFileChange}
                  className="p-3 border border-gray-300 rounded w-full"
                />
              </div>

              {/* Display Selected Files */}
              {files.length > 0 && (
                <div className="mt-4">
                  <h3 className="font-medium mb-2">Selected Files:</h3>
                  <ul className="list-disc list-inside text-gray-700">
                    {files.map((file, index) => (
                      <li key={index}>{file.name}</li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Buttons */}
              <div className="flex justify-between mt-4">
                <button
                  type="button"
                  onClick={closeModal}
                  className="bg-gray-300 hover:bg-gray-400 text-gray-700 font-medium px-5 py-3 rounded-lg w-1/2 mr-2"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="bg-blue-600 hover:bg-blue-700 text-white font-medium px-5 py-3 rounded-lg w-1/2 text-center flex items-center justify-center"
                >
                  {isEditing ? (networkLoading ? <ButtonLoader /> : "Update") : (networkLoading ? <ButtonLoader /> : "Save")}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default DoctorsManagement;
